/**
 * Ephemeral live-event toasts for the public/shared match analytics view.
 *
 * Distinct from the cockpit's single-slot ToastNotification: this stack
 * supports several toasts at once and throttles bursts (e.g. a poll that
 * catches up several events at once after a dropped connection) by only
 * promoting one queued toast into the visible stack at a time.
 */
import { useCallback, useEffect, useState } from "react";
import type { MatchEvent } from "../../store/useMatchLogStore";
import type { Match } from "../../pages/logger/types";
import { formatDisplayName } from "../../lib/nameFormat";

export type PublicToastKind =
  | "goal"
  | "yellowCard"
  | "redCard"
  | "substitution"
  | "shotOnTarget"
  | "penalty"
  | "varOverturned"
  | "varReview";

export interface PublicToastItem {
  id: string;
  kind: PublicToastKind;
  message: string;
}

type TFunction = (key: string, options?: Record<string, unknown>) => string;

const VARIANT_ACCENT: Record<PublicToastKind, string> = {
  goal: "border-amber-400",
  yellowCard: "border-yellow-400",
  redCard: "border-red-500",
  substitution: "border-blue-400",
  shotOnTarget: "border-slate-400",
  penalty: "border-purple-400",
  varOverturned: "border-cyan-400",
  varReview: "border-cyan-400",
};

const resolvePlayerLabel = (
  match: Match | null,
  playerId: string | undefined,
): string => {
  if (!playerId || !match) return "";
  const roster = [...match.home_team.players, ...match.away_team.players];
  const player = roster.find((p) => p.id === playerId);
  if (!player) return "";
  return player.short_name || formatDisplayName(player.full_name) || "";
};

/**
 * i18next interpolates {{token}} automatically when a real instance is
 * initialized, but the test environment's uninitialized fallback `t` returns
 * the defaultValue template verbatim. This is a no-op once i18next has
 * already substituted the tokens, and a safety net when it hasn't.
 */
const interpolateFallback = (
  text: string,
  vars: Record<string, string>,
): string =>
  Object.entries(vars).reduce(
    (out, [key, value]) => out.split(`{{${key}}}`).join(value),
    text,
  );

const resolveTeamLabel = (
  match: Match | null,
  teamId: string | undefined,
): string => {
  if (!match || !teamId) return "";
  if (teamId === match.home_team.id)
    return match.home_team.short_name || match.home_team.name;
  if (teamId === match.away_team.id)
    return match.away_team.short_name || match.away_team.name;
  return "";
};

/** Classify a live event into a toast-worthy kind, or null if it should not toast. */
export const classifyPublicToastEvent = (
  event: MatchEvent,
): PublicToastKind | null => {
  if (event.type === "Shot" && event.data?.outcome === "Goal") return "goal";
  // PMA-COCK-194/PMA-PUB-016: a keeper save is still a shot on target — the
  // client's complaint was that it produced no toast at all ("esto no sale
  // todavía como remate al arco en las notificaciones"). Checked after the
  // Goal branch above, which still wins for an actual goal.
  if (
    event.type === "Shot" &&
    (event.data?.outcome === "OnTarget" || event.data?.outcome === "Saved")
  )
    return "shotOnTarget";
  if (event.type === "Card") {
    const cardType = String(event.data?.card_type || "").toLowerCase();
    if (cardType.includes("yellow")) return "yellowCard";
    if (cardType.includes("red")) return "redCard";
    return null;
  }
  if (event.type === "Substitution") return "substitution";
  if (event.type === "SetPiece" && event.data?.set_piece_type === "Penalty")
    return "penalty";
  if (
    event.type === "SetPiece" &&
    event.data?.set_piece_type === "Goal Kick" &&
    event.data?.var_overturned_from === "Corner"
  )
    return "varOverturned";
  if (event.type === "GameStoppage" && event.data?.stoppage_type === "VARStart")
    return "varReview";
  return null;
};

/** Build the localized toast message for a classified event. */
export const buildPublicToastMessage = (
  kind: PublicToastKind,
  event: MatchEvent,
  match: Match | null,
  t: TFunction,
): string => {
  switch (kind) {
    case "goal": {
      const player =
        resolvePlayerLabel(match, event.player_id) ||
        resolveTeamLabel(match, event.team_id);
      const vars = { player };
      return interpolateFallback(
        t("publicToast.goal", {
          ns: "logger",
          defaultValue: "Goal - {{player}}",
          ...vars,
        }),
        vars,
      );
    }
    case "yellowCard": {
      const player =
        resolvePlayerLabel(match, event.player_id) ||
        resolveTeamLabel(match, event.team_id);
      const vars = { player };
      return interpolateFallback(
        t("publicToast.yellowCard", {
          ns: "logger",
          defaultValue: "Yellow card - {{player}}",
          ...vars,
        }),
        vars,
      );
    }
    case "redCard": {
      const player =
        resolvePlayerLabel(match, event.player_id) ||
        resolveTeamLabel(match, event.team_id);
      const vars = { player };
      return interpolateFallback(
        t("publicToast.redCard", {
          ns: "logger",
          defaultValue: "Red card - {{player}}",
          ...vars,
        }),
        vars,
      );
    }
    case "substitution": {
      const playerIn = resolvePlayerLabel(match, event.data?.player_on_id);
      const playerOut = resolvePlayerLabel(match, event.data?.player_off_id);
      const vars = { in: playerIn || "?", out: playerOut || "?" };
      return interpolateFallback(
        t("publicToast.substitution", {
          ns: "logger",
          defaultValue: "Substitution - {{in}} on for {{out}}",
          ...vars,
        }),
        vars,
      );
    }
    case "shotOnTarget": {
      const player =
        resolvePlayerLabel(match, event.player_id) ||
        resolveTeamLabel(match, event.team_id);
      const vars = { player };
      return interpolateFallback(
        t("publicToast.shotOnTarget", {
          ns: "logger",
          defaultValue: "Shot on target - {{player}}",
          ...vars,
        }),
        vars,
      );
    }
    case "penalty": {
      const team = resolveTeamLabel(match, event.team_id);
      const vars = { team };
      return interpolateFallback(
        t("publicToast.penalty", {
          ns: "logger",
          defaultValue: "Penalty - {{team}}",
          ...vars,
        }),
        vars,
      );
    }
    case "varOverturned":
      return t("publicToast.varOverturned", {
        ns: "logger",
        defaultValue: "VAR: Corner → Goal Kick",
      });
    case "varReview":
      return t("publicToast.varReview", {
        ns: "logger",
        defaultValue: "VAR review in progress",
      });
  }
};

const MAX_CONCURRENT_TOASTS = 3;
const DISPATCH_STAGGER_MS = 350;
const TOAST_DURATION_MS = 4500;

/**
 * Kinds that never reach the corner toast stack (PMA-COCK-187, extended
 * 2026-09-24 AC8): a goal, a yellow card and a red card now each get their
 * own centred `PublicGoalOverlay` card instead, so diverting them here
 * keeps the page from announcing the same event twice. Every other kind is
 * unaffected. `classifyPublicToastEvent` still returns `"goal"`/
 * `"yellowCard"`/`"redCard"` for those events — it is exported and
 * unit-tested on its own — only the queue's `enqueue` consults this list.
 */
export const TOAST_SUPPRESSED_KINDS: PublicToastKind[] = [
  "goal",
  "yellowCard",
  "redCard",
];

/**
 * Queue/throttle hook: enqueue() appends toast-worthy events (dropping any
 * item whose kind is in TOAST_SUPPRESSED_KINDS); the stack only ever shows
 * up to MAX_CONCURRENT_TOASTS at once, staggering the rest so a burst of
 * near-simultaneous events does not all pop in on top of each other.
 */
export function usePublicToastQueue() {
  const [queue, setQueue] = useState<PublicToastItem[]>([]);
  const [active, setActive] = useState<PublicToastItem[]>([]);

  const enqueue = useCallback((items: PublicToastItem[]) => {
    const admitted = items.filter(
      (item) => !TOAST_SUPPRESSED_KINDS.includes(item.kind),
    );
    if (admitted.length === 0) return;
    setQueue((prev) => [...prev, ...admitted]);
  }, []);

  const dismiss = useCallback((id: string) => {
    setActive((prev) => prev.filter((item) => item.id !== id));
  }, []);

  useEffect(() => {
    if (queue.length === 0 || active.length >= MAX_CONCURRENT_TOASTS) return;
    const timer = setTimeout(() => {
      setQueue((prev) => prev.slice(1));
      setActive((prev) => (queue[0] ? [...prev, queue[0]] : prev));
    }, DISPATCH_STAGGER_MS);
    return () => clearTimeout(timer);
  }, [queue, active.length]);

  return { active, enqueue, dismiss };
}

interface PublicEventToastStackProps {
  toasts: PublicToastItem[];
  onDismiss: (id: string) => void;
}

export default function PublicEventToastStack({
  toasts,
  onDismiss,
}: PublicEventToastStackProps) {
  return (
    <div
      className="fixed bottom-4 right-4 z-40 flex flex-col-reverse gap-2 pointer-events-none max-w-xs w-full"
      aria-live="polite"
      aria-atomic="false"
      data-testid="public-event-toast-stack"
    >
      {toasts.map((toast) => (
        <PublicEventToastItem
          key={toast.id}
          toast={toast}
          onDismiss={() => onDismiss(toast.id)}
        />
      ))}
    </div>
  );
}

function PublicEventToastItem({
  toast,
  onDismiss,
}: {
  toast: PublicToastItem;
  onDismiss: () => void;
}) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const showTimer = setTimeout(() => setVisible(true), 10);
    const hideTimer = setTimeout(
      () => setVisible(false),
      Math.max(0, TOAST_DURATION_MS - 250),
    );
    const removeTimer = setTimeout(onDismiss, TOAST_DURATION_MS);
    return () => {
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
      clearTimeout(removeTimer);
    };
    // react-hooks/exhaustive-deps is off project-wide; intentionally keyed
    // only on toast.id — onDismiss is an inline `() => dismiss(id)` closure
    // that gets a new identity on every parent re-render (e.g. the page's
    // 1s clock tick), so including it would reset this toast's timers.
  }, [toast.id]);

  return (
    <div
      className={`pointer-events-auto rounded-lg border-l-4 ${
        VARIANT_ACCENT[toast.kind]
      } bg-slate-900/95 text-slate-100 text-sm px-3 py-2 shadow-lg backdrop-blur-sm transition-all duration-300 ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"
      }`}
      role="status"
      data-testid="public-event-toast"
      data-toast-kind={toast.kind}
    >
      {toast.message}
    </div>
  );
}
